import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { endpoints } from '@main/access-control-types';
import { buildUseQuery, logger } from '@main/core-ui';
import { ONE_SECOND } from '@main/utils';

import { selectUser } from '../../Auth/App/selectors';

const PAGE_SIZE = 20;
const useGlobalActionItems = buildUseQuery(
  endpoints.globalActionItems,
  'GlobalActionItemsCount',
  {
    totalCount: null,
    unrolledCount: null,
  },
);

export const useActionItemStatusCount = (): {
  /** the returned count */
  count?: number;
  /** gql loading state */
  loading: boolean;
  /** gql error state */
  error: any;
} => {
  const user = useSelector(selectUser);
  const { data, loading, error } = useGlobalActionItems({
    variables: {
      first: PAGE_SIZE,
      filterBy: {
        resolved: false,
        ...(user?.isAdmin ? {} : { assigneeUserIds: [user!.id] }),
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !user,
  });

  // log error so we can debug. no need to show on screen, there will just be no badge
  useEffect(() => {
    if (error) {
      logger.error(error);
    }
  }, [error]);

  // number of action items to notify about
  return {
    count: data && data.totalCount > 0 ? data.totalCount : undefined,
    loading,
    error,
  };
};

const MAX_INTERCOM_LOAD_TIME = ONE_SECOND * 5;

/**
 * hook to poll to check if intercom is loaded
 *
 * @returns if it's fully loaded
 */
export const useIntercomLoaded = (): {
  /** is intercom loaded yet */
  intercomLoaded: boolean;
  /** is intercom failed to load in the required time window */
  intercomFailed: boolean;
} => {
  const [intercomLoaded, setIntercomLoaded] = useState(false);
  const [intercomFailed, setIntercomFailed] = useState(false);
  const timer = useRef<number>();
  const startTime = useRef(Date.now());

  useEffect(() => {
    const checkIntercomLoaded = (): void => {
      if (typeof Intercom !== 'undefined' || intercomLoaded) {
        setIntercomLoaded(true);
        if (timer.current) {
          clearInterval(timer.current);
        }
      }

      if (Date.now() - startTime.current > MAX_INTERCOM_LOAD_TIME) {
        setIntercomFailed(true);
        if (timer.current) {
          clearInterval(timer.current);
        }
      }
    };
    // poll handle every 100ms to check if it's defined
    timer.current = setInterval(checkIntercomLoaded, 100) as any;
    // also check immediately
    checkIntercomLoaded();

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = undefined;
      }
    };
  }, []);

  return { intercomLoaded, intercomFailed };
};
